<template>
  <div>
    <Spinner :loading="loading">{{ $t("Fetching Report") }}...</Spinner>
    <v-layout fill-height>
      <v-flex xs2 md3 class="fill-height report--sideCard">
        <Card class="fill-height">
          <v-card-title>
            <span class="title font-weight-light pa-2">{{
              $t("REPORTS")
            }}</span>
            <v-spacer></v-spacer>
          </v-card-title>
          <v-divider />
          <v-flex pa-3>
            <div v-if="paramTemplate.length === 0">
              {{ $t("No template configured") }}
            </div>
            <v-treeview
              v-model="templateSelected"
              :items="paramTemplate"
              selected-color="#E65100"
              return-object
              open-on-click
              hoverable
              style="overflow: auto"
            >
              <template v-slot:label="{ item }">
                <div
                  :class="{ 'accent--text': isActive(item) }"
                  @click="templateChange(item)"
                >
                  <v-icon v-show="isActive(item)"> chevron_right </v-icon>
                  {{ $t(item.name) }}
                </div>
              </template>
            </v-treeview>
          </v-flex>
        </Card>
      </v-flex>
      <v-flex xs10 md9>
        <Card class="fill-height" v-if="templateSelected.length > 0">
          <ReportContent
            @loading:report="loadingStateHandler"
            :key="componentKey"
            :selectedTemplate="selectedTemplate"
            :activeFilter="activeFilter"
          />
        </Card>
        <Card class="fill-height" v-else>
          <div class="image">
            <img
              src="../assets/file.svg"
              alt="information"
              height="60%"
              width="60%"
            />
          </div>
        </Card>
      </v-flex>
    </v-layout>
  </div>
</template>

<script>
const momentTz = require("moment-timezone");
const moment = require("moment");
import CustomReport from "@/services/customReport";

import ReportContent from "@/components/CustomReport/ReportContent";
import Spinner from "@/components/CustomReport/Spinner";

import { mapGetters } from "vuex";

export default {
  components: {
    ReportContent,
    Spinner,
  },
  props: {
    changesFromFilter: {
      type: Object,
      default: function () {
        return {};
      },
    },
  },
  data() {
    return {
      loading: false,
      componentKey: 0,
      templateSelected: [],
      activeFilter: {},
      templateList: [],
      selectedTemplate: {},
    };
  },
  mounted() {
    this.loadTemplates(this.getSelectedPlantId);
  },
  computed: {
    ...mapGetters([
      "getSelectedPlantId",
      "getActivePlantTimezone"
    ]),
    filterOption() {
      if (this.selectedTemplate._id.includes("SlotReport")) {
        return [
          {
            id: "Date",
            type: "datePicker",
            title: "Date",
          },
        ];
      }
      if (this.selectedTemplate._id.includes("PRGT")) {
        return [
          {
            id: "Date",
            type: "singleDatePicker",
            title: "Date"
          }
        ];
      }
      return [
        {
          id: "selectedRange",
          type: "select",
          title: "Choose Date Range",
          value: this.range,
        },
        {
          ref: "selectedRange",
          id: "Date",
          type: "datePicker",
          title: "Select Date Range",
          condition: (ref) => {
            if (
              (ref &&
                ref.text &&
                ref.text !== "1 min" &&
                !ref.hasOwnProperty("startOffset")) ||
              ref.text === "Custom"
            ) {
              return true;
            } else {
              return false;
            }
          },
        },
        {
          ref: "selectedRange",
          id: "Date",
          type: "singleDatePicker",
          title: "Select Date",
          condition: (ref) => {
            if (ref && ref.text && ref.text === "1 min") {
              return true;
            } else {
              return false;
            }
          },
        },
        {
          ref: "selectedRange",
          id: "DateFormat",
          type: "select",
          title: "Select Date Format",
          value: (ref) => {
            switch (ref.interval) {
              case "daily":
              case "hourly":
              case "minute":
                return [
                  "YYYY-MM-DD HH:mm",
                  "DD-MM-YYYY HH:mm",
                  "HH:mm YYYY-MM-DD ",
                  "HH:mm DD-MM-YYYY ",
                ];
              case "monthly":
                return ["YYYY-MM-DD", "DD-MM-YYYY"];
              case "yearly":
                return ["YYYY-MM", "MM-YYYY"];
              case "custom":
                return [
                  "YYYY-MM-DD HH:mm",
                  "DD-MM-YYYY HH:mm",
                  "HH:mm YYYY-MM-DD ",
                  "HH:mm DD-MM-YYYY ",
                ];
            }
          },
        },
      ];
    },
    range() {
      if (!Object.keys(this.selectedTemplate).length) {
        return [];
      }
      if (
        this.selectedTemplate._id.includes("GenReport") ||
        this.selectedTemplate._id.includes("SummaryReport")
      ) {
        return [
          {
            text: "Today",
            interval: "daily",
            startOffset: 0,
            endOffset: 0,
            format: "minute",
          },
          {
            text: "Yesterday",
            interval: "daily",
            startOffset: 1,
            endOffset: 1,
            format: "minute",
          },
          {
            text: "Last 3 days",
            interval: "daily",
            startOffset: 2,
            endOffset: 0,
            format: "daily",
          },
          {
            text: "Last 7 days",
            interval: "daily",
            startOffset: 6,
            endOffset: 0,
            format: "daily",
          },
          {
            text: "Last 30 days",
            interval: "daily",
            startOffset: 29,
            endOffset: 0,
            format: "daily",
          },
          {
            text: "Last 3 months",
            interval: "monthly",
            offsetLevel: "month",
            startOffset: 2,
            endOffset: 0,
            format: "monthly",
          },
          {
            text: "Present Year",
            interval: "monthly",
            offsetLevel: "year",
            startOffset: 0,
            endOffset: 0,
            format: "monthly",
          },
          {
            text: "Last year",
            interval: "monthly",
            offsetLevel: "year",
            startOffset: 1,
            endOffset: 1,
            format: "monthly",
          },
          {
            text: "Custom",
            interval: "custom",
          },
        ];
      }

      const range = [
        {
          text: "hourly",
          interval: "hourly",
          intervalGap: 60,
          format: "hourly",
        },
        {
          text: "30 mins",
          interval: "minute",
          intervalGap: 30,
          format: "minute",
        },
        {
          text: "15 mins",
          interval: "minute",
          intervalGap: 15,
          format: "minute",
        },
        {
          text: "10 mins",
          interval: "minute",
          intervalGap: 10,
          format: "minute",
        },
        {
          text: "5 mins",
          interval: "minute",
          intervalGap: 5,
          format: "minute",
        },
      ];
      if (this.selectedTemplate._id.includes("DeviceReport")) {
        range.push({
          text: "1 min",
          interval: "minute",
          intervalGap: 1,
          format: "minute",
        });
      }

      return range;
    },
    paramTemplate() {
      let newArr = [
        {
          name: "Plant Level",
          id: "Plant",
          children: [],
          node: "parent",
        },
        {
          name: "Block Level",
          id: "Block",
          children: [],
          node: "parent",
        },
        {
          name: "Device Level",
          id: "Device",
          children: [],
          node: "parent",
        },
      ];
      const device = ["inv", "em", "mfm", "scb"];
      let filteredArr = [];
      if (this.templateList.length > 0) {
        newArr.forEach((ar) => {
          this.templateList.forEach((el) => {
            let activeTemplate = el.title.split(" ");
            if (
              ar["name"].split(" ")[0] == "Device" &&
              device.includes(activeTemplate[0].toLowerCase())
            ) {
              ar.children.push({
                name: el.title,
                id: el.title,
              });
            }
            if (ar["name"].split(" ")[0] !== activeTemplate[0]) {
              return;
            }
            ar.children.push({
              name:
                activeTemplate[1] == "Level"
                  ? el.title.replace(
                      `${activeTemplate[0]} ${activeTemplate[1]}`,
                      ""
                    )
                  : el.title.replace(activeTemplate[0], ""),
              id: el.title,
            });
          });
        });

        filteredArr = newArr.filter((el) => el.children.length > 0);
      }
      return filteredArr;
    },
  },
  methods: {
    loadingStateHandler(val) {
      this.loading = val;
    },
    isActive(item) {
      return (
        this.templateSelected.length > 0 &&
        item.id === this.templateSelected[0].id
      );
    },
    templateChange(templateTemp) {
      if (templateTemp.node !== "parent") {
        this.templateSelected = [templateTemp];
        this.selectedTemplate = this.templateList.filter(
          (e) => e.title == templateTemp.id
        )[0];

        const Obj = {};
        const cloneFilterOption = _.cloneDeep(this.filterOption);
        cloneFilterOption.forEach((child) => {
          if (
            (child.condition &&
              (!!child.considerStateAlways ||
                child.condition(Obj[child.ref]))) ||
            !child.condition
          ) {
            if (child.type === "select") {
              if (typeof child.value === "function") {
                Obj[child.id] = child.value(Obj[child.ref])[0];
              } else {
                Obj[child.id] = child.value[0];
              }
            } else if (
              child.type === "datePicker" ||
              child.type === "singleDatePicker"
            ) {
              let parameters = ["from", "to"];
              parameters.forEach((key) => {
                if (!Obj[child.id]) {
                  Obj[child.id] = {};
                }
                Obj[child.id][key] = momentTz()
                  .tz(this.getActivePlantTimezone)
                  .format("YYYY-MM-DD");
              });
            } else {
              Obj[child.id] = child.value;
            }
          }
        });

        this.activeFilter = Obj;
        this.$emit("filter:config", cloneFilterOption);
        this.$emit("initial:active_filter", Obj);
        this.$emit("callback:onIntervalChange", this.dateRangeSelectionHandler)

        this.componentKey++;
      }
    },
    loadTemplates() {
      CustomReport.fetchPlantTemplate(this.getSelectedPlantId)
        .then((response) => {
          this.templateList = response.data.list;
        })
        .catch((err) => {
          this.templateList = [];
        });
    },
    dateRangeSelectionHandler(active) {
      let minimumDateAllowed = '', dateRangeAllowed = 31
      if (!active) {
        return { minimumDateAllowed, dateRangeAllowed };
      }
      if (
        this.selectedTemplate &&
        this.selectedTemplate._id &&
        this.selectedTemplate._id.includes("PRGT")
      ) {
        dateRangeAllowed = 1;
        minimumDateAllowed = moment()
          .subtract(90, "day")
          .format("YYYY-MM-DD");

        return { minimumDateAllowed, dateRangeAllowed };
      }
      if (active["Date"]) {
        if (
          active["selectedRange"].intervalGap &&
          active["selectedRange"].intervalGap == 1
        ) {
          dateRangeAllowed = 1;
          minimumDateAllowed = moment()
            .subtract(90, "day")
            .format("YYYY-MM-DD");
        } else if (
          active["selectedRange"].intervalGap &&
          active["selectedRange"].intervalGap == 5
        ) {
          dateRangeAllowed = 15;
        } else {
          dateRangeAllowed = moment(
            active["Date"]["from"]
          ).endOf("month")
            .format("DD");
        }
      }

      return {
        minimumDateAllowed,
        dateRangeAllowed
      }
    }
  },
  watch: {
    changesFromFilter: {
      handler(val) {
        this.activeFilter = val;
      },
      deep: true,
    },
    getSelectedPlantId() {
      this.activeFilter = {};
      this.templateList = [];
      this.templateSelected = [];
      this.selectedTemplate = {};
      this.$emit("filter:config", []);
      this.$emit("initial:active_filter", this.activeFilter);
      this.loadTemplates(this.getSelectedPlantId);
    },
  },
};
</script>

<style scoped>
.report--sideCard {
  height: calc(100vh - 190px);
}

.image {
  padding-top: 50px;
  text-align: center;
}
</style>

